import React, { useState, useEffect } from 'react';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import Store from 'store/store';
import api from 'api';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { PastPerformanceType } from 'store/modules/InvestmentAnalyser/types';
import PastPerformance from 'components/IPS/PastPerformance';
import PastPerformanceTable from 'components/IPS/PastPerformanceTable/PastPerformanceTable';
import { getCachableData } from './utils';
import { getStartDate } from 'components/PastPerformance/utils';

const { selectors, actions } = Store;

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '25rem'
  }
});

interface PerformanceTabContentProps {
  investments: any[];
  pastPerformance: PastPerformanceType;
  updatePastPerformance: Function;
  pastPerformanceCacheIsValid: boolean;
  selectedInvestment?: number;
  performancePeriod: string | null;
  provider?: any;
  range?: any;
}

const PerformanceTabContent = ({
  investments,
  pastPerformance,
  updatePastPerformance,
  pastPerformanceCacheIsValid,
  selectedInvestment,
  performancePeriod,
  provider,
  range
}: PerformanceTabContentProps) => {
  const classes = useStyles();
  const [data, setData] = useState(pastPerformance.data);
  const [errors, setErrors] = useState(pastPerformance.errors || []);
  const [limits, setLimits] = useState(pastPerformance.limits || []);
  const [tabularData, setTabularData] = useState(
    pastPerformance.tabularData || {}
  );
  const [earliestDates, setEarliestDates] = useState(
    pastPerformance.earliestDates || {}
  );
  const [footprints, setFootprints] = useState(
    pastPerformance.footprints || {}
  );
  const [isLoading, setIsLoading] = useState(
    !pastPerformance.investments.length
  );

  const setPastPerformanceData = (data: any) => {
    setData(data.data);
    setErrors(data.failed_codes);
    setLimits(data.limits);
    setTabularData(data.tabular_data || tabularData);
    setEarliestDates(data.earliest_dates || earliestDates);
    setFootprints(data.footprints || footprints);
    updatePastPerformance({
      data: data.data,
      errors: data.failed_codes,
      limits: data.limits,
      tabularData: data.tabular_data || tabularData,
      investments,
      earliestDates: data.earliest_dates || earliestDates,
      footprints: data.footprints || footprints
    });
  };

  useEffect(() => {
    if (!selectedInvestment && !provider) return;
    getCachableData(
      investments,
      pastPerformanceCacheIsValid,
      setIsLoading,
      selectedInvestment
        ? () =>
            api.getResearchItemPastPerformance({
              id: selectedInvestment,
              data: performancePeriod
                ? {
                    date_data: {
                      date: getStartDate(performancePeriod).format('DD/MM/YYYY')
                    }
                  }
                : {}
            })
        : () =>
            api.getMpsHubPastPerformance({
              provider: provider.id,
              range: range
            }),
      (response: any) => setPastPerformanceData(response.data)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investments]);

  return (
    <div>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <CircularProgress size="80px" />
        </div>
      ) : (
        <div style={{ paddingTop: '1rem' }}>
          <PastPerformance
            data={data}
            errors={errors}
            limits={limits}
            earliestDates={earliestDates}
            footprints={footprints}
            isAnyPolicyAdded={investments.length > 0}
            getPastPerformanceData={
              selectedInvestment
                ? api.getResearchItemPastPerformance
                : api.getMpsHubPastPerformance
            }
            setData={setPastPerformanceData}
            id={selectedInvestment}
            provider={provider}
            range={range}
            localStorageKey={`fintegrate-pp-ia-${selectedInvestment}-toggles`}
            canManageBenchmarks={!!selectedInvestment}
          />
          <div style={{ margin: '1rem 1rem 0 1rem' }}>
            <PastPerformanceTable data={tabularData} hideMaxDrawDown={true} />
          </div>
        </div>
      )}
    </div>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
      pastPerformance:
        selectors.investmentAnalyserSelectors.pastPerformance(state),
      pastPerformanceCacheIsValid:
        selectors.investmentAnalyserSelectors.pastPerformanceCacheIsValid(
          state
        ),
      performancePeriod:
        selectors.investmentAnalyserSelectors.performancePeriod(state)
    }),
    {
      updatePastPerformance:
        actions.investmentAnalyserActions.updatePastPerformance
    }
  )
)(PerformanceTabContent);
